import type { Lang } from '@/types';

const LOCALE_MAP: Record<Lang, string> = {
  uz: 'uz-UZ',
  ru: 'ru-RU',
  en: 'en-US',
};

/** Minglik ajratgich: uz/ru — bo'linmas bo'sh joy (U+00A0), en — vergul. */
const GROUP_SEPARATOR: Record<Lang, string> = {
  uz: String.fromCharCode(0xa0),
  ru: String.fromCharCode(0xa0),
  en: ',',
};

/**
 * Katta sonlarni ajratgichlar bilan formatlaydi (12847 → "12 847").
 *
 * Intl.NumberFormat ishlatilmaydi: Node (prerender, to'liq ICU) va Chrome (qisqartirilgan
 * ICU) uz-UZ ni turlicha formatlaydi ("129 000" va "129,000") — hydration mos kelmaydi.
 */
export function formatNumber(value: number, lang: Lang = 'uz'): string {
  const [int, frac] = String(Math.abs(value)).split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR[lang]);
  const decimal = lang === 'en' ? '.' : ',';
  return `${value < 0 ? '-' : ''}${grouped}${frac ? decimal + frac : ''}`;
}

/** Pulni so'mda formatlaydi. */
export function formatSom(value: number, lang: Lang = 'uz'): string {
  return `${formatNumber(value, lang)} so'm`;
}

/** O'zbekcha oy nomlari — Intl da uz uchun ma'lumot yo'q (pastdagi izohga qarang). */
const UZ_MONTHS = [
  'yanvar',
  'fevral',
  'mart',
  'aprel',
  'may',
  'iyun',
  'iyul',
  'avgust',
  'sentabr',
  'oktabr',
  'noyabr',
  'dekabr',
];

/**
 * ISO sanani "16 iyul 2026" ko'rinishiga o'giradi.
 *
 * O'zbekcha qo'lda formatlanadi: Chromium da `Intl.DateTimeFormat` uz uchun oy
 * nomlarini bilmaydi va "2026 M07 16" qaytaradi. ru/en da Intl to'g'ri ishlaydi.
 */
export function formatDate(iso: string, lang: Lang = 'uz'): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';

  if (lang === 'uz') {
    return `${d.getDate()} ${UZ_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  }

  return new Intl.DateTimeFormat(LOCALE_MAP[lang], {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(d);
}

/** "3 daqiqa oldin" kabi nisbiy vaqt. */
/** O'zbekcha birlik nomlari — Intl da uz uchun ma'lumot yo'q (pastdagi izohga qarang). */
const UZ_UNIT: Record<'minute' | 'hour' | 'day', string> = {
  minute: 'daqiqa',
  hour: 'soat',
  day: 'kun',
};

/**
 * "21 soat oldin" ko'rinishidagi nisbiy vaqt.
 *
 * O'zbekcha qo'lda formatlanadi: Chromium da `Intl.RelativeTimeFormat`
 * uz lokalini "qo'llab-quvvatlanadi" deb e'lon qiladi, lekin tarjima
 * ma'lumoti yo'q va xom `-21 h` qaytaradi. ru/en da Intl to'g'ri ishlaydi.
 */
export function timeAgo(iso: string, lang: Lang = 'uz'): string {
  const diffMs = new Date(iso).getTime() - Date.now();
  const diffMin = Math.round(diffMs / 60000);
  const diffHour = Math.round(diffMin / 60);
  const diffDay = Math.round(diffHour / 24);

  let value: number;
  let unit: 'minute' | 'hour' | 'day';
  if (Math.abs(diffMin) < 60) {
    value = diffMin;
    unit = 'minute';
  } else if (Math.abs(diffHour) < 24) {
    value = diffHour;
    unit = 'hour';
  } else {
    value = diffDay;
    unit = 'day';
  }

  if (lang === 'uz') {
    const n = Math.abs(value);
    if (n === 0) return 'hozir';
    return value < 0 ? `${n} ${UZ_UNIT[unit]} oldin` : `${n} ${UZ_UNIT[unit]}dan keyin`;
  }

  return new Intl.RelativeTimeFormat(LOCALE_MAP[lang], { numeric: 'auto' }).format(value, unit);
}

/** Sekundlarni "mm:ss" ko'rinishiga. */
export function formatDuration(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, '0');
  const s = Math.floor(totalSeconds % 60)
    .toString()
    .padStart(2, '0');
  return `${m}:${s}`;
}
